/**
 * useMembersFilters Hook
 * 
 * Custom hook for managing member filters (search, province, city, date range) and URL sync.
 */

import * as React from "react";
import { useLocation } from "wouter";

export function useMembersFilters(
  lockedProvince?: string,
  onFilterChange?: () => void,
  lockedCity?: string
) {
  const [location, setLocation] = useLocation();
  
  // Search filter
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Location filters - if lockedProvince is provided, always use it
  const [selectedProvince, setSelectedProvince] = React.useState<string | undefined>(lockedProvince);
  const [selectedCity, setSelectedCity] = React.useState<string | undefined>(lockedCity);
  
  // Date range filters
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined);
  const [dateField, setDateField] = React.useState<"tanggal_terbit" | "masa_laku" | "masa_laku_kta">("masa_laku");
  
  // Tipe IAR filter
  const [tipeIar, setTipeIar] = React.useState<string | undefined>(undefined);
  
  // Sort filters
  const [sortBy, setSortBy] = React.useState<"nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id">("_id");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  
  // Use refs to track latest values for callbacks
  const selectedProvinceRef = React.useRef<string | undefined>(undefined);
  const selectedCityRef = React.useRef<string | undefined>(undefined);
  const searchQueryRef = React.useRef<string>("");
  const startDateRef = React.useRef<Date | undefined>(undefined);
  const endDateRef = React.useRef<Date | undefined>(undefined);
  const dateFieldRef = React.useRef<"tanggal_terbit" | "masa_laku" | "masa_laku_kta">("masa_laku");
  const tipeIarRef = React.useRef<string | undefined>(undefined);
  const sortByRef = React.useRef<"nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id">("_id");
  const sortOrderRef = React.useRef<"asc" | "desc">("asc");
  
  // Keep refs in sync with state
  React.useEffect(() => {
    selectedProvinceRef.current = selectedProvince;
    selectedCityRef.current = selectedCity;
    searchQueryRef.current = searchQuery;
    startDateRef.current = startDate;
    endDateRef.current = endDate;
    dateFieldRef.current = dateField;
    tipeIarRef.current = tipeIar;
    sortByRef.current = sortBy;
    sortOrderRef.current = sortOrder;
  }, [selectedProvince, selectedCity, searchQuery, startDate, endDate, dateField, tipeIar, sortBy, sortOrder]);
  
  // Read filters from URL query string on mount only
  // This should only run once on initial mount, not when URL changes (URL changes are handled by updateFiltersInUrl)
  const hasInitializedRef = React.useRef(false);
  
  React.useEffect(() => {
    // Only run once on mount, skip if already initialized
    if (hasInitializedRef.current) {
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("search") || "";
    // Use URL param if it exists, otherwise fallback to lockedProvince
    const provinceParam = urlParams.get("province") || lockedProvince || undefined;
    const cityParam = urlParams.get("city") || lockedCity || undefined;
    const startDateParam = urlParams.get("startDate");
    const endDateParam = urlParams.get("endDate");
    const dateFieldParam = urlParams.get("dateField") as "tanggal_terbit" | "masa_laku" | "masa_laku_kta" | null;
    const tipeIarParam = urlParams.get("tipe_iar") || undefined;
    const sortByParam = urlParams.get("sortBy") as "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id" | null;
    const sortOrderParam = urlParams.get("sortOrder") as "asc" | "desc" | null;
    
    setSearchQuery(searchParam);
    // Use province from URL if it exists, otherwise use lockedProvince
    setSelectedProvince(provinceParam);
    setSelectedCity(cityParam);
    
    // Parse date params
    if (startDateParam) {
      const parsedDate = new Date(startDateParam);
      if (!isNaN(parsedDate.getTime())) {
        setStartDate(parsedDate);
      }
    }
    if (endDateParam) {
      const parsedDate = new Date(endDateParam);
      if (!isNaN(parsedDate.getTime())) {
        setEndDate(parsedDate);
      }
    }
    
    // Parse date field param
    if (dateFieldParam && ["tanggal_terbit", "masa_laku", "masa_laku_kta"].includes(dateFieldParam)) {
      setDateField(dateFieldParam);
    }
    
    // Parse tipe_iar param
    if (tipeIarParam) {
      setTipeIar(tipeIarParam);
    }
    
    // Parse sort params
    if (sortByParam && ["nama_lengkap", "callsign", "tanggal_terbit", "masa_laku", "provinsi", "status_callsign", "status_keanggotaan", "_id"].includes(sortByParam)) {
      setSortBy(sortByParam);
    }
    if (sortOrderParam && ["asc", "desc"].includes(sortOrderParam)) {
      setSortOrder(sortOrderParam);
    }
    
    // Initialize refs
    searchQueryRef.current = searchParam;
    selectedProvinceRef.current = provinceParam;
    selectedCityRef.current = cityParam;
    startDateRef.current = startDateParam ? new Date(startDateParam) : undefined;
    endDateRef.current = endDateParam ? new Date(endDateParam) : undefined;
    dateFieldRef.current = dateFieldParam && ["tanggal_terbit", "masa_laku", "masa_laku_kta"].includes(dateFieldParam) ? dateFieldParam : "masa_laku";
    tipeIarRef.current = tipeIarParam;
    sortByRef.current = sortByParam && ["nama_lengkap", "callsign", "tanggal_terbit", "masa_laku", "provinsi", "status_callsign", "status_keanggotaan", "_id"].includes(sortByParam) ? sortByParam : "_id";
    sortOrderRef.current = sortOrderParam && ["asc", "desc"].includes(sortOrderParam) ? sortOrderParam : "asc";
    
    hasInitializedRef.current = true;
  }, []); // Only run once on mount - URL changes are handled by updateFiltersInUrl, not by re-reading from URL

  React.useEffect(() => {
    if (lockedProvince) {
      setSelectedProvince(lockedProvince);
      selectedProvinceRef.current = lockedProvince;
    }
  }, [lockedProvince]);

  React.useEffect(() => {
    if (lockedCity) {
      setSelectedCity(lockedCity);
      selectedCityRef.current = lockedCity;
    }
  }, [lockedCity]);

  // Update URL query string when filters change
  const updateFiltersInUrl = React.useCallback((
    search: string,
    province?: string,
    city?: string,
    startDate?: Date,
    endDate?: Date,
    dateField?: "tanggal_terbit" | "masa_laku" | "masa_laku_kta",
    tipeIar?: string
  ) => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Update search
    if (search.trim()) {
      urlParams.set("search", search.trim());
    } else {
      urlParams.delete("search");
    }
    
    // Update province - use selected province if provided, otherwise fallback to lockedProvince only if province is explicitly undefined
    // If province is explicitly passed (even if undefined), use it; otherwise use lockedProvince as fallback
    const provinceToSet = province !== undefined ? province : (lockedProvince || undefined);
    if (provinceToSet) {
      urlParams.set("province", provinceToSet);
    } else {
      urlParams.delete("province");
    }
    
    const cityToSet = city !== undefined ? city : lockedCity;
    if (cityToSet) {
      urlParams.set("city", cityToSet);
    } else {
      urlParams.delete("city");
    }
    
    // Update date range
    if (startDate) {
      urlParams.set("startDate", startDate.toISOString().split("T")[0] || "");
    } else {
      urlParams.delete("startDate");
    }
    
    if (endDate) {
      urlParams.set("endDate", endDate.toISOString().split("T")[0] || "");
    } else {
      urlParams.delete("endDate");
    }
    
    // Update date field
    if (dateField && dateField !== "masa_laku") {
      urlParams.set("dateField", dateField);
    } else {
      urlParams.delete("dateField");
    }
    
    // Update tipe_iar
    if (tipeIar && tipeIar.trim()) {
      urlParams.set("tipe_iar", tipeIar.trim());
    } else {
      urlParams.delete("tipe_iar");
    }
    
    // Note: sort params are not updated here, use updateSort() instead
    
    // Remove pagination params when filters change
    urlParams.delete("cursor");
    urlParams.delete("direction");
    
    const newSearch = urlParams.toString();
    const basePath = location.split("?")[0] || "";
    const newUrl = newSearch ? `${basePath}?${newSearch}` : basePath;
    setLocation(newUrl, { replace: true });
    
    // Notify that filters have changed (e.g., to reset cursor)
    if (onFilterChange) {
      onFilterChange();
    }
  }, [location, setLocation, lockedProvince, lockedCity, onFilterChange]);

  // Update URL with pagination cursor while preserving filters
  const updateUrlWithCursor = React.useCallback((cursor: string | null, direction: "next" | "previous") => {
    const urlParams = new URLSearchParams();
    
    // Preserve search filter
    if (searchQuery.trim()) {
      urlParams.set("search", searchQuery.trim());
    }
    
    // Preserve location filters - use selected province, but fallback to lockedProvince if no province selected
    const provinceToPreserve = selectedProvince || lockedProvince;
    if (provinceToPreserve) {
      urlParams.set("province", provinceToPreserve);
    }
    const cityToPreserve = selectedCity || lockedCity;
    if (cityToPreserve) {
      urlParams.set("city", cityToPreserve);
    }
    
    // Preserve date range filters
    if (startDate) {
      urlParams.set("startDate", startDate.toISOString().split("T")[0] || "");
    }
    if (endDate) {
      urlParams.set("endDate", endDate.toISOString().split("T")[0] || "");
    }
    
    // Preserve date field
    if (dateField && dateField !== "masa_laku") {
      urlParams.set("dateField", dateField);
    }
    
    // Preserve tipe_iar filter
    if (tipeIar && tipeIar.trim()) {
      urlParams.set("tipe_iar", tipeIar.trim());
    }
    
    // Add cursor for pagination
    if (cursor) {
      urlParams.set("cursor", cursor);
      urlParams.set("direction", direction);
    }

    urlParams.set("sortBy", sortBy);
    urlParams.set("sortOrder", sortOrder);
    
    const newSearch = urlParams.toString();
    const basePath = location.split("?")[0] || "";
    const newUrl = newSearch ? `${basePath}?${newSearch}` : basePath;
    setLocation(newUrl, { replace: true });
  }, [
    location,
    setLocation,
    searchQuery,
    selectedProvince,
    selectedCity,
    startDate,
    endDate,
    dateField,
    tipeIar,
    lockedProvince,
    lockedCity,
    sortBy,
    sortOrder,
  ]);

  const clearFilters = React.useCallback(() => {
    setSearchQuery("");
    // Preserve lockedProvince if it exists
    setSelectedProvince(lockedProvince);
    setSelectedCity(lockedCity);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateField("masa_laku");
    setTipeIar(undefined);
    setSortBy("_id");
    setSortOrder("asc");
    // Preserve lockedProvince in URL when clearing
    updateFiltersInUrl("", lockedProvince, lockedCity, undefined, undefined, "masa_laku", undefined);
  }, [updateFiltersInUrl, lockedProvince, lockedCity]);
  
  const updateSort = React.useCallback((field: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id", order: "asc" | "desc") => {
    setSortBy(field);
    setSortOrder(order);
    
    // Update URL with sort params
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("sortBy", field);
    urlParams.set("sortOrder", order);
    urlParams.delete("cursor");
    urlParams.delete("direction");
    
    const newSearch = urlParams.toString();
    const basePath = location.split("?")[0] || "";
    const newUrl = newSearch ? `${basePath}?${newSearch}` : basePath;
    setLocation(newUrl, { replace: true });
    
    // Notify that filters have changed (e.g., to reset cursor)
    if (onFilterChange) {
      onFilterChange();
    }
  }, [location, setLocation, onFilterChange]);

  return {
    // State
    searchQuery,
    setSearchQuery,
    selectedProvince,
    setSelectedProvince,
    selectedCity,
    setSelectedCity,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    dateField,
    setDateField,
    tipeIar,
    setTipeIar,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    // Refs
    selectedProvinceRef,
    selectedCityRef,
    searchQueryRef,
    startDateRef,
    endDateRef,
    dateFieldRef,
    tipeIarRef,
    sortByRef,
    sortOrderRef,
    // Functions
    updateFiltersInUrl,
    updateUrlWithCursor,
    clearFilters,
    updateSort,
  };
}

